import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fastapiService } from '../../services/fastapiService';
import { ADMIN_BASE } from '../../config/adminLayers';

type TdsStatus = 'pending_certificate' | 'uploaded' | 'matched' | 'mismatch' | 'chasing';

type TdsInvoice = {
  id: string;
  invoice_number?: string;
  currency?: string;
  amount?: number;
  total_amount?: number;
  tds_amount?: number;
  tan?: string;
  tds_rate?: number;
  tds_section?: string;
  amount_received?: number;
  tds_certificate_status?: TdsStatus;
  tds_certificate_type?: string;
  tds_certificate_number?: string;
  tds_certificate_filename?: string;
  tds_certificate_amount?: number;
  tds_form26as_amount?: number;
  tds_certificate_notes?: string;
  tds_certificate_uploaded_at?: string;
  tds_certificate_matched_at?: string;
  tds_certificate_chased_at?: string;
  has_certificate_file?: boolean;
  created_at?: string;
  workspaces?: { name?: string };
  user_profiles?: { email?: string; full_name?: string };
};

type TdsEvent = {
  id: string;
  event_type?: string;
  status?: string;
  notes?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
};

const STATUS_TABS: { id: string; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'pending_certificate', label: 'Pending certificate' },
  { id: 'uploaded', label: 'Uploaded' },
  { id: 'mismatch', label: 'Mismatch' },
  { id: 'chasing', label: 'Chasing' },
  { id: 'matched', label: 'Matched' },
  { id: 'all', label: 'All TDS' },
];

const statusClass = (status?: string) => {
  switch (status) {
    case 'matched':
      return 'bg-green-100 text-green-700';
    case 'mismatch':
      return 'bg-red-100 text-red-700';
    case 'chasing':
      return 'bg-amber-100 text-amber-800';
    case 'uploaded':
      return 'bg-sky-100 text-sky-700';
    case 'pending_certificate':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-admin-100 text-admin-700';
  }
};

const statusLabel = (status?: string) => {
  switch (status) {
    case 'pending_certificate':
      return 'Pending certificate';
    case 'uploaded':
      return 'Uploaded';
    case 'matched':
      return 'Matched';
    case 'mismatch':
      return 'Mismatch';
    case 'chasing':
      return 'Chasing';
    default:
      return status || 'Unknown';
  }
};

export const TdsFollowupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const invoiceIdFromUrl = (searchParams.get('invoice_id') || '').trim();
  const invoicesBase = `${ADMIN_BASE}/invoices`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<TdsInvoice[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('open');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [openCount, setOpenCount] = useState(0);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [selected, setSelected] = useState<TdsInvoice | null>(null);
  const [events, setEvents] = useState<TdsEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certType, setCertType] = useState('form_16a');
  const [certNumber, setCertNumber] = useState('');
  const [certAmount, setCertAmount] = useState('');
  const [form26asAmount, setForm26asAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [summaryRes, listRes] = await Promise.all([
          fastapiService.getTdsFollowupSummary(),
          fastapiService.getTdsFollowupInvoices({
            page,
            limit,
            status: activeTab,
            search: search || undefined,
            invoice_id: invoiceIdFromUrl || undefined,
          }),
        ]);
        const summary = (summaryRes as any)?.data || summaryRes;
        setSchemaMissing(Boolean(summary?.schema_missing || (listRes as any)?.schema_missing));
        setCounts(summary?.counts || {});
        setOpenCount(summary?.open || 0);
        setRows(((listRes as any)?.data || []) as TdsInvoice[]);
        setTotal((listRes as any)?.pagination?.total || 0);
      } catch (e: any) {
        setError(e?.error || e?.message || 'Failed to load TDS follow-up');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page, limit, search, activeTab, invoiceIdFromUrl, refreshKey]);

  useEffect(() => {
    if (!invoiceIdFromUrl || rows.length === 0) return;
    const match = rows.find((row) => row.id === invoiceIdFromUrl);
    if (match && selected?.id !== match.id) {
      void openDetail(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceIdFromUrl, rows]);

  const tabCounts = useMemo(
    () => ({
      open: openCount,
      all: Object.values(counts).reduce((sum, n) => sum + (n || 0), 0),
      ...counts,
    }),
    [counts, openCount],
  );

  const formatMoney = (amount?: number | string | null, currency = 'INR') => {
    const value = parseFloat(String(amount ?? 0));
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(Number.isFinite(value) ? value : 0);
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const hydrateForm = (inv: TdsInvoice) => {
    setCertType(inv.tds_certificate_type || 'form_16a');
    setCertNumber(inv.tds_certificate_number || '');
    setCertAmount(inv.tds_certificate_amount != null ? String(inv.tds_certificate_amount) : '');
    setForm26asAmount(inv.tds_form26as_amount != null ? String(inv.tds_form26as_amount) : '');
    setNotes(inv.tds_certificate_notes || '');
    setFile(null);
  };

  const openDetail = async (inv: TdsInvoice) => {
    setSelected(inv);
    hydrateForm(inv);
    setDetailLoading(true);
    try {
      const res = await fastapiService.getTdsFollowupInvoice(inv.id);
      const data = (res as any)?.data || inv;
      setSelected(data);
      hydrateForm(data);
      setEvents(((res as any)?.events || []) as TdsEvent[]);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to load invoice detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const parsedAmount = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : undefined;
  };

  const refreshAfterSave = async (invoiceId: string) => {
    setRefreshKey((k) => k + 1);
    const res = await fastapiService.getTdsFollowupInvoice(invoiceId);
    const data = (res as any)?.data;
    if (data) {
      setSelected(data);
      hydrateForm(data);
    }
    setEvents(((res as any)?.events || []) as TdsEvent[]);
  };

  const runAction = async (action: 'match' | 'mismatch' | 'chase' | 'note') => {
    if (!selected) return;
    try {
      setSaving(true);
      setError('');
      await fastapiService.applyTdsFollowupAction(selected.id, {
        action,
        certificate_type: certType,
        certificate_number: certNumber || undefined,
        certificate_amount: parsedAmount(certAmount),
        form26as_amount: parsedAmount(form26asAmount),
        notes: notes || undefined,
      });
      await refreshAfterSave(selected.id);
    } catch (e: any) {
      setError(e?.error || e?.response?.data?.detail || e?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadCertificate = async () => {
    if (!selected || !file) {
      setError('Choose a Form 16A / Form 131 file to upload');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await fastapiService.uploadTdsCertificate(selected.id, file, {
        certificate_type: certType,
        certificate_number: certNumber || undefined,
        certificate_amount: certAmount || undefined,
        form26as_amount: form26asAmount || undefined,
        notes: notes || undefined,
      });
      await refreshAfterSave(selected.id);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">TDS / 26AS follow-up</h1>
        <p className="mt-1 text-sm text-admin-600">
          Track Form 16A / Form 131 certificates, match 26AS amounts to invoice TDS, and chase missing certificates.
        </p>
      </div>

      {schemaMissing && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Migration 719 is not applied yet. Invoice list and uploads will stay empty until the TDS certificate columns exist.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          { id: 'pending_certificate', label: 'Pending certificate' },
          { id: 'uploaded', label: 'Uploaded' },
          { id: 'mismatch', label: 'Mismatch' },
          { id: 'chasing', label: 'Chasing' },
          { id: 'matched', label: 'Matched' },
        ].map((card) => (
          <button
            key={card.id}
            type="button"
            onClick={() => {
              setPage(1);
              setActiveTab(card.id);
            }}
            className={`admin-card p-4 text-left ${activeTab === card.id ? 'ring-2 ring-primary-400' : ''}`}
          >
            <div className="text-xs uppercase tracking-wide text-admin-500">{card.label}</div>
            <div className="mt-1 text-2xl font-semibold text-admin-900">{counts[card.id] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="admin-card p-6">
        <div className="border-b border-admin-200 mb-4">
          <nav className="flex flex-wrap gap-4 -mb-px">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setPage(1);
                  setActiveTab(tab.id);
                }}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-admin-500 hover:text-admin-700 hover:border-admin-300'
                }`}
              >
                {tab.label}
                <span className="ml-2 text-xs text-admin-400">{tabCounts[tab.id] ?? 0}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search invoice number or TAN…"
            className="admin-input flex-1 min-w-[200px] max-w-md"
          />
          <Link to={invoicesBase} className="text-sm text-sky-700 hover:text-sky-900 font-medium">
            All invoices
          </Link>
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">TAN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">TDS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Certificate / 26AS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {loading ? (
                <tr>
                  <td className="px-4 py-4 text-admin-500" colSpan={7}>
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-4 text-admin-500" colSpan={7}>
                    No TDS invoices in this queue.
                  </td>
                </tr>
              ) : (
                rows.map((inv) => (
                  <tr key={inv.id} className="hover:bg-admin-50">
                    <td className="px-4 py-4 text-sm font-mono text-admin-900">
                      {inv.invoice_number || `INV-${inv.id.substring(0, 8).toUpperCase()}`}
                      <div className="text-xs text-admin-500">{inv.workspaces?.name || '—'}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-admin-900">
                      <div>{inv.user_profiles?.email || '—'}</div>
                      {inv.user_profiles?.full_name && (
                        <div className="text-xs text-admin-500">{inv.user_profiles.full_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-mono text-admin-700">{inv.tan || '—'}</td>
                    <td className="px-4 py-4 text-sm font-medium text-admin-900">
                      {formatMoney(inv.tds_amount, inv.currency)}
                      <div className="text-xs text-admin-500">
                        Invoice {formatMoney(inv.amount || inv.total_amount, inv.currency)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-admin-700">
                      <div>Cert {inv.tds_certificate_amount != null ? formatMoney(inv.tds_certificate_amount, inv.currency) : '—'}</div>
                      <div className="text-xs text-admin-500">
                        26AS {inv.tds_form26as_amount != null ? formatMoney(inv.tds_form26as_amount, inv.currency) : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass(inv.tds_certificate_status)}`}>
                        {statusLabel(inv.tds_certificate_status)}
                      </span>
                      {inv.tds_certificate_chased_at && (
                        <div className="mt-1 text-xs text-admin-500">Chased {formatDate(inv.tds_certificate_chased_at)}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <button
                        type="button"
                        className="text-sky-700 hover:text-sky-900 font-medium"
                        onClick={() => openDetail(inv)}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-admin-600">
            Page {page} of {Math.max(1, Math.ceil(total / limit))} · {total} invoices
          </div>
          <div className="flex items-center gap-2">
            <button className="admin-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <button
              className="admin-button"
              disabled={page >= Math.ceil(total / limit)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-admin-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-admin-900">Certificate / 26AS review</h2>
                <p className="text-sm text-admin-500 font-mono">
                  {selected.invoice_number || `INV-${String(selected.id).slice(0, 8).toUpperCase()}`}
                </p>
              </div>
              <button type="button" className="text-admin-500 hover:text-admin-800" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
            <div className="max-h-[75vh] overflow-y-auto px-5 py-4 space-y-5">
              {detailLoading ? (
                <p className="text-sm text-admin-500">Loading…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                    <div>
                      <div className="text-xs text-admin-500">Invoice TDS</div>
                      <div className="font-medium text-admin-900">{formatMoney(selected.tds_amount, selected.currency)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-admin-500">Amount received</div>
                      <div className="font-medium text-admin-900">{formatMoney(selected.amount_received, selected.currency)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-admin-500">TAN</div>
                      <div className="font-mono text-admin-900">{selected.tan || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-admin-500">Status</div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass(selected.tds_certificate_status)}`}>
                        {statusLabel(selected.tds_certificate_status)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-sm">
                      <span className="mb-1 block text-admin-600">Certificate type</span>
                      <select value={certType} onChange={(e) => setCertType(e.target.value)} className="admin-input w-full">
                        <option value="form_16a">Form 16A</option>
                        <option value="form_131">Form 131</option>
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block text-admin-600">Certificate number</span>
                      <input value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="admin-input w-full" />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block text-admin-600">Certificate TDS amount</span>
                      <input
                        value={certAmount}
                        onChange={(e) => setCertAmount(e.target.value)}
                        className="admin-input w-full"
                        inputMode="decimal"
                        placeholder={String(selected.tds_amount ?? '')}
                      />
                    </label>
                    <label className="text-sm">
                      <span className="mb-1 block text-admin-600">26AS TDS amount</span>
                      <input
                        value={form26asAmount}
                        onChange={(e) => setForm26asAmount(e.target.value)}
                        className="admin-input w-full"
                        inputMode="decimal"
                      />
                    </label>
                    <label className="text-sm md:col-span-2">
                      <span className="mb-1 block text-admin-600">Notes</span>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="admin-input w-full min-h-[72px]"
                        placeholder="Chase note, mismatch explanation, or 26AS remark"
                      />
                    </label>
                    <label className="text-sm md:col-span-2">
                      <span className="mb-1 block text-admin-600">Upload Form 16A / Form 131</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-admin-700"
                      />
                      {selected.has_certificate_file && (
                        <button
                          type="button"
                          className="mt-2 text-sm text-sky-700 hover:text-sky-900 font-medium"
                          onClick={() => fastapiService.downloadTdsCertificate(selected.id).catch((e) => setError(e?.message || 'Download failed'))}
                        >
                          Download current file{selected.tds_certificate_filename ? ` (${selected.tds_certificate_filename})` : ''}
                        </button>
                      )}
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="admin-button" disabled={saving} onClick={uploadCertificate}>
                      {saving ? 'Saving…' : 'Upload certificate'}
                    </button>
                    <button type="button" className="admin-button" disabled={saving} onClick={() => runAction('match')}>
                      Mark matched
                    </button>
                    <button type="button" className="admin-button" disabled={saving} onClick={() => runAction('mismatch')}>
                      Mark mismatch
                    </button>
                    <button
                      type="button"
                      className="admin-button"
                      disabled={saving || selected.tds_certificate_status === 'matched'}
                      onClick={() => runAction('chase')}
                    >
                      Mark chasing
                    </button>
                    <button type="button" className="admin-button" disabled={saving} onClick={() => runAction('note')}>
                      Save note
                    </button>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-admin-800">History</h3>
                    {events.length === 0 ? (
                      <p className="mt-2 text-sm text-admin-500">No uploads, matches, or chase notes yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {events.map((event) => (
                          <li key={event.id} className="rounded-md border border-admin-200 p-3 text-sm">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-admin-800">{event.event_type}</span>
                              {event.status && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(event.status)}`}>
                                  {statusLabel(event.status)}
                                </span>
                              )}
                              <span className="text-xs text-admin-500">{formatDate(event.created_at)}</span>
                            </div>
                            {event.notes && <div className="mt-1 text-admin-700">{event.notes}</div>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
